import { GroupByMode } from '@griply/types';
import { useCallback } from 'react';
import { useUpdateHabitGoalTaskOrderMutation } from 'shared/hooks/queries/use-update-habit-goal-task-order-mutation';
import { useUpdateHabitLifeAreaTaskOrderMutation } from 'shared/hooks/queries/use-update-habit-life-area-task-order-mutation';
import { useUpdateHabitOrderMutation } from 'shared/hooks/queries/use-update-habit-order-mutation';
import { useUpdateHabitPriorityTaskOrderMutation } from 'shared/hooks/queries/use-update-habit-priority-task-order-mutation';
import { useUpdateTasksMutation } from 'shared/hooks/queries/use-update-tasks-mutation';
import { ID } from 'shared/types/id';
import { UpdateTask } from 'shared/types/task';
import { TaskPriorityOptions } from 'shared/types/task-base';

export const useHabitsOnReorder = (groupBy: GroupByMode | undefined | null) => {
  const { mutate: updateHabitOrder } = useUpdateHabitOrderMutation();
  const { mutate: updateGoalOrder } = useUpdateHabitGoalTaskOrderMutation();
  const { mutate: updateLifeAreaOrder } =
    useUpdateHabitLifeAreaTaskOrderMutation();
  const { mutate: updatePriorityOrder } =
    useUpdateHabitPriorityTaskOrderMutation();
  const { mutate: updateTasks } = useUpdateTasksMutation();

  return useCallback(
    (
      updatedSections: { id: ID; taskSorting: ID[] }[],
      updatedTasks: UpdateTask[],
    ) => {
      // Cross-group drags carry the habit's goal/priority/life-area field change.
      if (updatedTasks.length) {
        updateTasks(updatedTasks);
      }

      if (groupBy === GroupByMode.Goal) {
        updateGoalOrder(
          updatedSections.map(({ id, taskSorting }) => ({
            goalId: id,
            taskIds: taskSorting,
          })),
        );
        return;
      }

      if (groupBy === GroupByMode.LifeArea) {
        updatedSections.forEach(({ id, taskSorting }) =>
          updateLifeAreaOrder({ id, taskIds: taskSorting }),
        );
        return;
      }

      if (groupBy === GroupByMode.Priority) {
        updatePriorityOrder(
          updatedSections.map(({ id, taskSorting }) => ({
            priority: id as TaskPriorityOptions,
            taskIds: taskSorting,
          })),
        );
        return;
      }

      updateHabitOrder(updatedSections[0]?.taskSorting ?? []);
    },
    [
      groupBy,
      updateGoalOrder,
      updateHabitOrder,
      updateLifeAreaOrder,
      updatePriorityOrder,
      updateTasks,
    ],
  );
};
